/**
 * rug-calculator.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Jaipur Rugs — Collapsible Product Page Calculator
 *
 * Module breakdown:
 *   RCConfig        — constants & conversion tables
 *   RCState         — single source of truth for calculator state
 *   RCDom           — DOM node cache (populated once on init)
 *   RCToggle        — accordion open/close with smooth animation
 *   RCUnits         — unit label & secondary-field visibility helpers
 *   RCShape         — shape selector + diagram updates
 *   RCCalc          — area & price computation + validation
 *   RCCart          — product-form intercept → injects _calculator_price attribute
 *   RCQuoteModal    — Get a Quote popup + Shopify /contact submission
 *   RCInit          — bootstrap, binds all modules
 *
 * Cart integration strategy:
 *   • A hidden input  name="properties[_calculator_price]"  lives inside the
 *     product <form>.  When Horizon submits via fetch (/cart/add), we intercept
 *     that request and ensure the property is present.
 *   • The leading underscore "_" hides the property from default cart UI.
 *   • The Shopify Function (rug-cart-transform) reads this property and applies:
 *       final_price = variant_price + calculator_price
 *
 * Pricing display strategy (strict rules):
 *   • We DO NOT override the variant price in the DOM.
 *   • We show only the CALCULATOR ADDITION and the TOTAL ESTIMATE inside
 *     the calculator panel itself.
 *   • The authoritative price is set server-side by the Shopify Function.
 * ─────────────────────────────────────────────────────────────────────────────
 */

(function (window, document) {
  'use strict';

  /* ══════════════════════════════════════════════════════════════
     RCConfig — constants
  ══════════════════════════════════════════════════════════════ */
  const RCConfig = Object.freeze({
    /** Convert any unit to feet (multiply input value by factor) */
    UNIT_TO_FT: {
      ft:   1,
      m:    3.28084,
      cm:   0.0328084,
      inch: 0.0833333,
    },

    /** Shapes rendered with a single "Diameter" input */
    ROUND_SHAPES: ['round'],

    /** Shapes where width === length (hide length field) */
    SQUARE_SHAPES: ['square'],

    /** Minimum dimension (sq-ft) to allow Add to Cart */
    MIN_AREA_SQFT: 0.1,
  });

  /* ══════════════════════════════════════════════════════════════
     RCState — mutable runtime state
  ══════════════════════════════════════════════════════════════ */
  const RCState = {
    isOpen:           false,
    isCalculated:     false,
    unit:             'ft',
    shape:            'rectangle',
    calculatedPrice:  0,   // additional price in paise (smallest INR unit)
    areaSqFt:         0,
    pricePerSqFt:     0,   // read from data attribute (rupees, float)
  };

  /* ══════════════════════════════════════════════════════════════
     RCDom — cached DOM references
  ══════════════════════════════════════════════════════════════ */
  const RCDom = {
    wrapper:         null,
    toggleBtn:       null,
    body:            null,

    unitSelect:      null,
    shapeSelect:     null,
    diagramShape:    null,
    diagWidthLbl:    null,
    diagLengthLbl:   null,

    dimsRow:         null,
    diamRow:         null,

    // Width
    widthA:          null,
    widthB:          null,
    widthBWrap:      null,
    tagWA:           null,

    // Length
    lengthA:         null,
    lengthB:         null,
    lengthBWrap:     null,
    tagLA:           null,

    // Diameter
    diamA:           null,
    diamB:           null,
    diamBWrap:       null,
    tagDA:           null,

    calcBtn:         null,
    result:          null,
    resultPrice:     null,
    leadTimeVal:     null,

    quoteTriggerWrap:null,
    quoteTriggerBtn: null,

    // hidden form property
    propCalcPrice:   null,

    // Quote modal
    quoteModal:      null,
    quoteModalClose: null,
    quoteForm:       null,
    quoteSuccess:    null,
    quoteCalcSummary:null,
  };

  /* ══════════════════════════════════════════════════════════════
     RCDom.query — populate cache
  ══════════════════════════════════════════════════════════════ */
  function queryDom() {
    const g = id => document.getElementById(id);
    RCDom.wrapper          = g('rug-calc-wrapper');
    RCDom.toggleBtn        = g('rug-calc-toggle');
    RCDom.body             = g('rug-calc-body');

    RCDom.unitSelect       = g('rc-unit');
    RCDom.shapeSelect      = g('rc-shape');
    RCDom.diagramShape     = g('rc-diagram-shape');
    RCDom.diagWidthLbl     = g('rc-diag-w');
    RCDom.diagLengthLbl    = g('rc-diag-l');

    RCDom.dimsRow          = g('rc-dims-row');
    RCDom.diamRow          = g('rc-diameter-row');

    RCDom.widthA           = g('rc-width-a');
    RCDom.widthB           = g('rc-width-b');
    RCDom.widthBWrap       = g('rc-width-b-wrap');
    RCDom.tagWA            = g('rc-tag-wa');

    RCDom.lengthA          = g('rc-length-a');
    RCDom.lengthB          = g('rc-length-b');
    RCDom.lengthBWrap      = g('rc-length-b-wrap');
    RCDom.tagLA            = g('rc-tag-la');

    RCDom.diamA            = g('rc-diam-a');
    RCDom.diamB            = g('rc-diam-b');
    RCDom.diamBWrap        = g('rc-diam-b-wrap');
    RCDom.tagDA            = g('rc-tag-da');

    RCDom.calcBtn          = g('rc-calculate-btn');
    RCDom.result           = g('rc-result');
    RCDom.resultPrice      = g('rc-result-price');
    RCDom.leadTimeVal      = g('rc-lead-time-val');

    RCDom.quoteTriggerWrap = g('rc-quote-trigger-wrap');
    RCDom.quoteTriggerBtn  = g('rc-quote-btn');

    RCDom.propCalcPrice    = g('rc-prop-calculator-price');

    RCDom.quoteModal       = g('rc-quote-modal');
    RCDom.quoteModalClose  = g('rc-quote-modal-close');
    RCDom.quoteForm        = g('rc-quote-form');
    RCDom.quoteSuccess     = g('rc-quote-success');
    RCDom.quoteCalcSummary = g('rc-q-calc-summary');
  }

  /* ══════════════════════════════════════════════════════════════
     RCToggle — accordion open / close
  ══════════════════════════════════════════════════════════════ */
  const RCToggle = {
    open() {
      RCState.isOpen = true;
      RCDom.toggleBtn.setAttribute('aria-expanded', 'true');
      RCDom.body.setAttribute('aria-hidden', 'false');
      RCDom.body.classList.add('is-open');
    },

    close() {
      RCState.isOpen = false;
      RCDom.toggleBtn.setAttribute('aria-expanded', 'false');
      RCDom.body.setAttribute('aria-hidden', 'true');
      RCDom.body.classList.remove('is-open');
    },

    toggle() {
      RCState.isOpen ? RCToggle.close() : RCToggle.open();
    },
  };

  /* ══════════════════════════════════════════════════════════════
     RCUnits — label badges & secondary field visibility
  ══════════════════════════════════════════════════════════════ */
  const RCUnits = {
    /** Returns true if the unit requires a secondary "Inch" sub-field */
    needsSecondary(unit) {
      return unit === 'ft';
    },

    /** Human-readable abbreviated label for a given unit */
    label(unit) {
      return { ft: 'Ft', m: 'M', cm: 'Cm', inch: 'Inch' }[unit] || unit;
    },

    apply(unit) {
      const lbl = RCUnits.label(unit);
      const showSec = RCUnits.needsSecondary(unit);

      // Update primary unit badges
      [RCDom.tagWA, RCDom.tagLA, RCDom.tagDA].forEach(el => {
        if (el) el.textContent = lbl;
      });

      // Toggle secondary (inch) pill visibility
      [RCDom.widthBWrap, RCDom.lengthBWrap, RCDom.diamBWrap].forEach(el => {
        if (!el) return;
        showSec ? el.classList.remove('rc-hidden') : el.classList.add('rc-hidden');
      });
    },
  };

  /* ══════════════════════════════════════════════════════════════
     RCShape — shape selector & diagram
  ══════════════════════════════════════════════════════════════ */
  const RCShape = {
    apply(shape) {
      const isRound  = RCConfig.ROUND_SHAPES.includes(shape);
      const isSquare = RCConfig.SQUARE_SHAPES.includes(shape);

      // Toggle dimension rows
      if (RCDom.dimsRow)  RCDom.dimsRow.classList.toggle('rug-calc__row--hidden', isRound);
      if (RCDom.diamRow)  RCDom.diamRow.classList.toggle('rug-calc__row--hidden', !isRound);

      // Hide length group for square shape
      const lengthGroup = document.getElementById('rc-length-group');
      if (lengthGroup) {
        lengthGroup.style.display = isSquare ? 'none' : '';
      }

      // Update diagram
      if (RCDom.diagramShape) {
        RCDom.diagramShape.setAttribute('data-shape', shape);
      }

      // Diagram labels
      if (RCDom.diagWidthLbl) {
        RCDom.diagWidthLbl.textContent = isRound ? 'Diameter' : 'Width';
      }
      if (RCDom.diagLengthLbl) {
        RCDom.diagLengthLbl.style.visibility = isRound ? 'hidden' : 'visible';
      }
    },
  };

  /* ══════════════════════════════════════════════════════════════
     RCCalc — area computation, price calculation, UI update
  ══════════════════════════════════════════════════════════════ */
  const RCCalc = {

    /**
     * Convert a (primary, secondary) pair to decimal feet.
     * For Ft unit: primary = whole feet, secondary = inches.
     * For other units: primary value × conversion factor, secondary ignored.
     */
    toDimensionFt(primary, secondary, unit) {
      const p = parseFloat(primary) || 0;
      const s = parseFloat(secondary) || 0;
      if (unit === 'ft') return p + s / 12;
      return p * (RCConfig.UNIT_TO_FT[unit] || 1);
    },

    /** Compute area in sq-ft based on selected shape and dimensions */
    computeArea({ shape, widthFt, lengthFt, diamFt }) {
      if (RCConfig.ROUND_SHAPES.includes(shape)) {
        return Math.PI * Math.pow(diamFt / 2, 2);
      }
      if (RCConfig.SQUARE_SHAPES.includes(shape)) {
        return widthFt * widthFt;
      }
      return widthFt * lengthFt;
    },

    /** Format paise amount as Indian Rupee string */
    formatINR(paise) {
      const rupees = paise / 100;
      return '₹' + rupees.toLocaleString('en-IN', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
    },

    /** Validate inputs; returns true if all required fields are filled */
    validate() {
      let valid = true;
      RCCalc.clearErrors();

      const isRound  = RCConfig.ROUND_SHAPES.includes(RCState.shape);
      const isSquare = RCConfig.SQUARE_SHAPES.includes(RCState.shape);

      if (isRound) {
        const dv = parseFloat(RCDom.diamA?.value) || 0;
        if (dv <= 0) {
          RCCalc.markError(RCDom.diamA, 'Enter a diameter');
          valid = false;
        }
      } else {
        const wv = parseFloat(RCDom.widthA?.value) || 0;
        if (wv <= 0) {
          RCCalc.markError(RCDom.widthA, 'Enter a width');
          valid = false;
        }
        if (!isSquare) {
          const lv = parseFloat(RCDom.lengthA?.value) || 0;
          if (lv <= 0) {
            RCCalc.markError(RCDom.lengthA, 'Enter a length');
            valid = false;
          }
        }
      }
      return valid;
    },

    markError(inputEl, msg) {
      if (!inputEl) return;
      const pill = inputEl.closest('.rug-calc__input-pill');
      if (pill) pill.classList.add('rc-error');
      // inject error message span after the pill's parent
      const container = pill?.parentElement;
      if (container && !container.querySelector('.rc-field-error')) {
        const span = document.createElement('span');
        span.className = 'rc-field-error';
        span.setAttribute('role', 'alert');
        span.textContent = msg;
        container.after(span);
      }
    },

    clearErrors() {
      document.querySelectorAll('.rug-calc__input-pill.rc-error')
        .forEach(el => el.classList.remove('rc-error'));
      document.querySelectorAll('.rc-field-error')
        .forEach(el => el.remove());
    },

    /** Main calculate handler — called when user clicks "Calculate" */
    run() {
      if (!RCCalc.validate()) return;

      const unit  = RCState.unit;
      const shape = RCState.shape;

      const widthFt  = RCCalc.toDimensionFt(RCDom.widthA?.value,  RCDom.widthB?.value,  unit);
      const lengthFt = RCCalc.toDimensionFt(RCDom.lengthA?.value, RCDom.lengthB?.value, unit);
      const diamFt   = RCCalc.toDimensionFt(RCDom.diamA?.value,   RCDom.diamB?.value,   unit);

      const areaSqFt = RCCalc.computeArea({ shape, widthFt, lengthFt, diamFt });

      if (areaSqFt < RCConfig.MIN_AREA_SQFT) {
        RCCalc.markError(RCDom.widthA, 'Dimensions too small');
        return;
      }

      // Price in rupees, then convert to paise (integer) for Shopify Functions
      const rupees      = RCState.pricePerSqFt * areaSqFt;
      const calcPaise   = Math.round(rupees * 100);

      // Update state
      RCState.calculatedPrice = calcPaise;
      RCState.areaSqFt        = areaSqFt;
      RCState.isCalculated    = true;

      // ── Update result UI ──────────────────────────────────────
      if (RCDom.resultPrice) {
        RCDom.resultPrice.textContent = RCCalc.formatINR(calcPaise);
      }
      if (RCDom.result) RCDom.result.hidden = false;

      // ── Inject into hidden form property ─────────────────────
      // Value must be in paise (integer) so the Shopify Function can add it
      // to the variant's base price without floating-point risk.
      if (RCDom.propCalcPrice) {
        RCDom.propCalcPrice.value = calcPaise;
      }

      // ── Toggle ATC / Get a Quote visibility ──────────────────
      RCCart.syncButtonVisibility();
    },

    /** Reset all calculated state (called when inputs change) */
    reset() {
      RCState.isCalculated    = false;
      RCState.calculatedPrice = 0;
      RCState.areaSqFt        = 0;

      if (RCDom.result) RCDom.result.hidden = true;
      if (RCDom.propCalcPrice) RCDom.propCalcPrice.value = '';

      RCCalc.clearErrors();
      RCCart.syncButtonVisibility();
    },

    /** Build a human-readable summary for the quote form */
    buildSummary() {
      const isRound  = RCConfig.ROUND_SHAPES.includes(RCState.shape);
      const isSquare = RCConfig.SQUARE_SHAPES.includes(RCState.shape);
      const unitLbl  = RCUnits.label(RCState.unit);
      const lines    = [
        `Product : ${document.title}`,
        `Shape   : ${RCState.shape}`,
        `Unit    : ${unitLbl}`,
      ];
      if (isRound) {
        lines.push(`Diameter: ${RCDom.diamA?.value || '—'} ${unitLbl}`);
      } else {
        lines.push(`Width   : ${RCDom.widthA?.value || '—'} ${unitLbl}`);
        if (!isSquare) lines.push(`Length  : ${RCDom.lengthA?.value || '—'} ${unitLbl}`);
      }
      if (RCState.areaSqFt > 0) {
        lines.push(`Area    : ${RCState.areaSqFt.toFixed(2)} sq ft`);
        lines.push(`Est.Price: ${RCCalc.formatINR(RCState.calculatedPrice)}`);
      }
      return lines.join('\n');
    },
  };

  /* ══════════════════════════════════════════════════════════════
     RCCart — Add to Cart button state + fetch intercept
  ══════════════════════════════════════════════════════════════ */
  const RCCart = {

    /**
     * Locate the Horizon "Add to Cart" submit button.
     * Horizon renders: <button type="submit" name="add" …>
     */
    getATCButton() {
      return document.querySelector(
        'form[action="/cart/add"] button[name="add"], ' +
        'form[action="/cart/add"] button[type="submit"]:not(#rc-quote-btn)'
      );
    },

    /**
     * Toggle the ATC button enabled/disabled and show/hide "Get a Quote"
     * based on whether the calculator has been completed.
     */
    syncButtonVisibility() {
      const atcBtn  = RCCart.getATCButton();
      const qWrap   = RCDom.quoteTriggerWrap;

      if (!atcBtn) return;

      if (RCState.isCalculated) {
        // Calculator done — enable ATC, hide quote prompt
        atcBtn.disabled = false;
        atcBtn.removeAttribute('title');
        if (qWrap) qWrap.style.display = 'none';
      } else {
        // Calculator not done — disable ATC, show quote prompt
        atcBtn.disabled = true;
        atcBtn.title    = 'Please complete the calculator to set a custom size.';
        if (qWrap) qWrap.style.display = '';
      }
    },

    /**
     * Intercept Horizon's fetch-based /cart/add call to ensure
     * properties[_calculator_price] is included.
     *
     * Horizon submits the product form via Shopify's cart API using fetch,
     * not a classic form POST. We monkey-patch window.fetch to inject the
     * hidden property whenever the request targets /cart/add.
     */
    patchFetch() {
      const _fetch = window.fetch.bind(window);

      window.fetch = async function (resource, init = {}, ...rest) {
        const url = typeof resource === 'string' ? resource
          : resource instanceof Request ? resource.url : '';

        if (url.includes('/cart/add') && RCState.isCalculated) {
          try {
            init = RCCart._injectProperty(init);
          } catch (e) {
            console.warn('[RugCalc] fetch patch error:', e);
          }
        }

        return _fetch(resource, init, ...rest);
      };
    },

    /**
     * Inject _calculator_price into the request body.
     * Handles three body formats used by Horizon:
     *   1. FormData
     *   2. URLSearchParams / URL-encoded string
     *   3. JSON string
     */
    _injectProperty(init) {
      let { body } = init;
      const pricePaise = RCState.calculatedPrice;

      if (body instanceof FormData) {
        body.set('properties[_calculator_price]', String(pricePaise));
        return { ...init, body };
      }

      if (body instanceof URLSearchParams) {
        body.set('properties[_calculator_price]', String(pricePaise));
        return { ...init, body };
      }

      if (typeof body === 'string') {
        if (body.trimStart().startsWith('{')) {
          // JSON body
          const obj = JSON.parse(body);
          obj.properties = obj.properties || {};
          obj.properties['_calculator_price'] = String(pricePaise);
          return { ...init, body: JSON.stringify(obj) };
        } else {
          // URL-encoded string
          const params = new URLSearchParams(body);
          params.set('properties[_calculator_price]', String(pricePaise));
          return { ...init, body: params.toString() };
        }
      }

      return init; // unknown format — pass through unchanged
    },

    /**
     * Ensure the hidden property input lives inside the product <form>.
     * (The snippet already places it there, but this is a safety net.)
     */
    ensurePropertyInForm() {
      const form  = document.querySelector('form[action="/cart/add"]');
      const input = RCDom.propCalcPrice;
      if (!form || !input) return;
      if (!form.contains(input)) form.appendChild(input);
    },
  };

  /* ══════════════════════════════════════════════════════════════
     RCQuoteModal — Get a Quote popup
  ══════════════════════════════════════════════════════════════ */
  const RCQuoteModal = {
    open() {
      if (!RCDom.quoteModal) return;
      // Auto-populate calculator summary
      if (RCDom.quoteCalcSummary) {
        RCDom.quoteCalcSummary.value = RCCalc.buildSummary();
      }
      RCDom.quoteModal.hidden = false;
      document.body.style.overflow = 'hidden';
      // focus first input
      setTimeout(() => RCDom.quoteModal.querySelector('input')?.focus(), 80);
    },

    close() {
      if (!RCDom.quoteModal) return;
      RCDom.quoteModal.hidden = true;
      document.body.style.overflow = '';
    },

    async submit(e) {
      e.preventDefault();
      const form     = RCDom.quoteForm;
      const nameEl   = document.getElementById('rc-q-name');
      const emailEl  = document.getElementById('rc-q-email');
      const nameErr  = document.getElementById('rc-q-name-err');
      const emailErr = document.getElementById('rc-q-email-err');
      const submitBtn= form.querySelector('.rc-quote-modal__submit');

      // Clear previous errors
      [nameEl, emailEl].forEach(el => el?.classList.remove('rc-error'));
      if (nameErr)  nameErr.textContent  = '';
      if (emailErr) emailErr.textContent = '';

      let valid = true;
      if (!nameEl?.value.trim()) {
        nameEl?.classList.add('rc-error');
        if (nameErr) nameErr.textContent = 'Name is required.';
        valid = false;
      }
      const emailRx = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailEl?.value.trim() || !emailRx.test(emailEl.value)) {
        emailEl?.classList.add('rc-error');
        if (emailErr) emailErr.textContent = 'Enter a valid email.';
        valid = false;
      }
      if (!valid) return;

      if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = 'Sending…'; }

      try {
        const fd = new FormData(form);
        // Ensure calculator summary is up to date
        fd.set('contact[body]',
          (document.getElementById('rc-q-msg')?.value || '') +
          '\n\n---\n' + RCCalc.buildSummary()
        );

        const res = await fetch('/contact#rc-quote-form', {
          method:  'POST',
          body:    fd,
          headers: { 'Accept': 'application/json' },
        });

        if (res.ok || res.redirected) {
          if (form)                  form.hidden = true;
          if (RCDom.quoteSuccess)    RCDom.quoteSuccess.hidden = false;
          setTimeout(() => RCQuoteModal.close(), 3200);
        } else {
          throw new Error('Server error: ' + res.status);
        }
      } catch (err) {
        console.error('[RugCalc] Quote submit failed:', err);
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = 'Send Request';
        }
        // Graceful fallback — open mailto
        const sub  = encodeURIComponent('Quote Request: ' + document.title);
        const body = encodeURIComponent(RCCalc.buildSummary());
        window.open(`mailto:?subject=${sub}&body=${body}`);
      }
    },
  };

  /* ══════════════════════════════════════════════════════════════
     RCInit — bootstrap
  ══════════════════════════════════════════════════════════════ */
  function init() {
    if (!document.getElementById('rug-calc-wrapper')) return;

    queryDom();

    // ── Read price-per-sqft from data attribute ───────────────
    const ppsfRaw = parseFloat(RCDom.wrapper?.dataset.pricePerSqft);
    RCState.pricePerSqFt = isNaN(ppsfRaw) ? 0 : ppsfRaw;

    // ── Initial UI state ──────────────────────────────────────
    RCUnits.apply(RCState.unit);
    RCShape.apply(RCState.shape);
    RCCart.syncButtonVisibility();      // ATC disabled by default
    RCCart.ensurePropertyInForm();
    RCCart.patchFetch();

    // ── TOGGLE BUTTON ─────────────────────────────────────────
    RCDom.toggleBtn?.addEventListener('click', RCToggle.toggle);

    // ── UNIT SELECT ───────────────────────────────────────────
    RCDom.unitSelect?.addEventListener('change', e => {
      RCState.unit = e.target.value;
      RCUnits.apply(RCState.unit);
      RCCalc.reset();
    });

    // ── SHAPE SELECT ──────────────────────────────────────────
    RCDom.shapeSelect?.addEventListener('change', e => {
      RCState.shape = e.target.value;
      RCShape.apply(RCState.shape);
      RCCalc.reset();
    });

    // ── DIMENSION INPUTS → reset on change ───────────────────
    const dimInputs = [
      RCDom.widthA, RCDom.widthB,
      RCDom.lengthA, RCDom.lengthB,
      RCDom.diamA, RCDom.diamB,
    ];
    dimInputs.forEach(el => el?.addEventListener('input', RCCalc.reset));

    // ── CALCULATE BUTTON ──────────────────────────────────────
    RCDom.calcBtn?.addEventListener('click', RCCalc.run);

    // ── GET A QUOTE ───────────────────────────────────────────
    RCDom.quoteTriggerBtn?.addEventListener('click', RCQuoteModal.open);
    RCDom.quoteModalClose?.addEventListener('click', RCQuoteModal.close);
    RCDom.quoteModal?.querySelector('.rc-quote-modal__backdrop')
         ?.addEventListener('click', RCQuoteModal.close);
    RCDom.quoteForm?.addEventListener('submit', RCQuoteModal.submit);

    // Close on Escape
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && RCDom.quoteModal && !RCDom.quoteModal.hidden) {
        RCQuoteModal.close();
      }
    });
  }

  // ── Boot ───────────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})(window, document);
